/**
 * VererbungTaskSection.tsx - the task «Vererbung prüfen» in the Aufgaben peek (P5-S7). The Vererbung or the
 * automatische Übertragung of a new policy failed (a lock timeout while a confirmation held the
 * registration, an error): the policy stands, but it does not follow its insurer's registration yet.
 * «Erneut prüfen» runs it again for the task's policy (POST /tasks/:taskId/vererbung/erneut-pruefen,
 * policy.write, the key of every write that fires it); the task is set done when it succeeds.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { ApiError, apiClient } from '../../lib/api-client';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';

/** The key of the route. */
export const VERERBUNG_RETRY_KEY = 'policy.write';

function errorText(err: unknown): string {
  if (err instanceof ApiError && err.code === 'vererbung_failed') return 'Die Vererbung ist erneut fehlgeschlagen. Bitte später nochmals versuchen.';
  if (err instanceof ApiError && err.status === 404) return 'Die Aufgabe ist bereits erledigt.';
  return 'Die Vererbung konnte nicht ausgeführt werden.';
}

export function VererbungTaskSection({ taskId }: { taskId: string }) {
  const { can } = useEffectivePermissions();
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: () => apiClient.policies.vererbungErneutPruefen(taskId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['policies'] });
      void qc.invalidateQueries({ queryKey: ['registrations'] });
    },
  });
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p className="text-muted-foreground">
        Die Eintragung beim Versicherer konnte nicht auf die Police übertragen werden. Die Police ist gespeichert; prüfen Sie die
        Vererbung erneut.
      </p>
      {can(VERERBUNG_RETRY_KEY) && (
        <div>
          <Button size="sm" disabled={retry.isPending} onClick={() => retry.mutate()}>
            Erneut prüfen
          </Button>
        </div>
      )}
      {retry.isSuccess && <p className="text-xs text-muted-foreground">Vererbung ausgeführt.</p>}
      {retry.isError && (
        <p className="text-xs text-destructive" role="alert">
          {errorText(retry.error)}
        </p>
      )}
    </div>
  );
}
